import { createContext, useContext } from 'react';

/**
 * FieldControlContext — how a field's control id reaches the control.
 *
 * FormField is the single owner of field identity: it mints the id (or takes an
 * explicit `htmlFor`) and publishes it here. A labelable single-control primitive
 * adopts that id as its element id, which is what makes the caption's `for` point
 * at something real.
 *
 * Only the `as="field"` shape publishes an id. Group and caption shapes publish
 * `null`, so a primitive rendered inside one keeps whatever id it would have had
 * on its own and no caption ever points at it.
 *
 * Native `<input>`/`<textarea>` children cannot consume context. Those call sites
 * use FormField's function-children form instead:
 *   <FormField label="Prompt">{({ controlId }) => <input id={controlId} />}</FormField>
 */
const FieldControlContext = createContext(null);

/**
 * Resolve the id a control should render with.
 *
 * Precedence, per the ratified contract: an explicit `id` prop always wins, then
 * the field context, then whatever fallback the primitive supplies for its
 * standalone (no FormField) case.
 *
 * @param {string} [explicitId] the control's own `id` prop, if the caller passed one
 * @param {string} [fallbackId] the primitive's internal id, used when standalone
 * @returns {string|undefined}
 */
export function useFieldControlId(explicitId, fallbackId) {
  const field = useContext(FieldControlContext);
  return explicitId || field?.controlId || fallbackId || undefined;
}

/**
 * Decide what `aria-label` a control should actually emit.
 *
 * An accessible name from `aria-label` OUTRANKS a `<label for>` association, so
 * a name that merely repeats the caption would mask the association we just
 * built — and would make a `getByLabelText` assertion pass on a control that has
 * no association at all.
 *
 * But "redundant" has to mean redundant. Several call sites pair a terse visible
 * caption with a deliberately fuller spoken name — "FPS" on screen, "Scene FPS"
 * announced — because the caption is disambiguated by its panel and the spoken
 * name is not. Dropping that would lose real information, so it survives.
 *
 * The association is worth keeping either way: `for` is what makes clicking the
 * caption focus the control, and that behavior is independent of the name.
 *
 * Two things can already name the control: the primitive's own `label` prop when
 * it renders standalone, or a FormField caption. Either counts.
 *
 * @param {string} [ariaLabel] the name the caller asked for
 * @param {string} [explicitId] the control's own id prop, if any
 * @param {*} [ownLabel] the primitive's own label prop, if it renders one
 * @returns {string|undefined} the aria-label to render
 */
export function useResolvedAriaLabel(ariaLabel, explicitId, ownLabel) {
  const field = useContext(FieldControlContext);
  if (!ariaLabel) return undefined;
  const ownCaption = typeof ownLabel === 'string' ? ownLabel : null;
  const fieldCaption = !explicitId && field?.controlId ? field.labelText : null;
  const caption = ownCaption || fieldCaption;
  // Only drop it when it says exactly what the caption already says. A fuller
  // spoken name beside a terse visible one ("Width" / "Frame width") is
  // deliberate, and outranking the association is the price of keeping it.
  const same = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();
  return caption && same(ariaLabel, caption) ? undefined : ariaLabel;
}

export default FieldControlContext;
